import { useEffect, useRef, useState } from 'react';
import bootstrapScript from 'virtual:microapp/guest-bootstrap';
import type { ApiClient } from '../api/client';
import type { ClientBundle } from '../api/types';
import { FRAME_SANDBOX, buildFrameHtml, createFrameNonce } from './frameHtml';
import { FrameHost, type FrameHostEvent } from './host';

export interface MicroappFrameProps {
  api: ApiClient;
  workspaceId: string;
  appId: string;
  client: ClientBundle;
  /** GET A/client のETag。無ければIf-Matchを付けない。 */
  clientDigest: string | null;
  title: string;
  /** 再読込を親から要求するためのキー。app.updatedやstale-clientで増やす。 */
  reloadKey?: number;
  onHostEvent?(event: FrameHostEvent): void;
}

type FrameStatus = 'connecting' | 'connected' | 'navigated';

interface FrameObservation {
  status: FrameStatus;
  rejections: string[];
  requests: number;
  lastRejectedCode: string | null;
}

/**
 * microappのclient UIを載せるiframe。
 *
 * iframe要素はReactに描かせず、効果の中で作って捨てる。
 * 同じ要素へsrcdocを入れ直すと、置き換えられる前の文書と新しい文書のどちらが
 * handshakeに勝つかが実行時のタイミングで変わり、接続できないframeが残ることがある
 * （React StrictModeの二重実行で実際に再現した）。
 * 1つのFrameHostが1つのiframe要素だけを持つようにして、その競合を無くしている。
 */
export function MicroappFrame(props: MicroappFrameProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [observation, setObservation] = useState<FrameObservation>({
    status: 'connecting',
    rejections: [],
    requests: 0,
    lastRejectedCode: null,
  });

  // handlerを毎回作り直してもeffectを張り直さないよう、最新値をrefへ寄せる。
  const latest = useRef(props);
  latest.current = props;

  const { workspaceId, appId, client, title, reloadKey } = props;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setObservation({ status: 'connecting', rejections: [], requests: 0, lastRejectedCode: null });

    const nonce = createFrameNonce();
    const iframe = document.createElement('iframe');
    iframe.className = 'microapp-frame';
    iframe.setAttribute('sandbox', FRAME_SANDBOX);
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    iframe.setAttribute('title', title);
    iframe.setAttribute('data-testid', 'microapp-frame');

    const host = new FrameHost({
      api: latest.current.api,
      workspaceId,
      appId,
      clientDigest: latest.current.clientDigest,
      nonce,
      onEvent: (event) => {
        latest.current.onHostEvent?.(event);
        setObservation((current) => reduceObservation(current, event));
      },
    });

    // DOMへ入れる前にsrcdocを決める。
    // 空のiframeを先に挿すと about:blank のloadが1回起きて、srcdocのloadと合わせて2回になる。
    // そのloadがhandshakeの後に届くと「frameが自分で遷移した」と誤検知する（実機で再現した）。
    iframe.srcdoc = buildFrameHtml({
      workspaceId,
      appId,
      javascript: client.javascript,
      css: client.css,
      title,
      bootstrapScript,
      nonce,
    });
    // handshakeを取りこぼさないよう、DOMへ入れる前に受け口を用意する。
    host.attach(iframe);
    container.appendChild(iframe);

    return () => {
      host.detach('frameの再構築');
      iframe.remove();
    };
  }, [workspaceId, appId, client, title, reloadKey]);

  return (
    <div className="microapp-frame-wrap">
      {observation.status === 'navigated' && (
        <div className="frame-escaped" role="alert" data-testid="frame-escaped">
          <p>microappの画面が別の文書へ遷移したため、通信を切断しました。</p>
          <p className="hint">安全のためこのframeは破棄されています。再読込すると復帰します。</p>
        </div>
      )}
      {/* iframeはeffectがここへ差し込む。navigated後は隠して操作できないようにする。 */}
      <div ref={containerRef} hidden={observation.status === 'navigated'} />
      <div className="frame-status" data-testid="frame-status">
        <span data-testid="frame-connection">{observation.status}</span>
        <span data-testid="frame-requests">要求 {observation.requests}</span>
        {observation.rejections.length > 0 && (
          <span data-testid="frame-rejections">拒否 {observation.rejections.join(', ')}</span>
        )}
        {observation.lastRejectedCode && (
          <span data-testid="frame-last-rejected">直近の拒否 {observation.lastRejectedCode}</span>
        )}
      </div>
    </div>
  );
}

function reduceObservation(current: FrameObservation, event: FrameHostEvent): FrameObservation {
  // navigatedは終端。ここから接続済みへ戻る表示にすると、破棄したはずのframeが
  // 生きているように見えてしまう。
  if (current.status === 'navigated') return current;
  switch (event.type) {
    case 'connected':
      return { ...current, status: 'connected' };
    case 'navigated':
      return { ...current, status: 'navigated' };
    case 'handshake-rejected':
      return { ...current, rejections: [...current.rejections, event.reason] };
    case 'request':
      return {
        ...current,
        requests: current.requests + 1,
        lastRejectedCode: event.ok ? current.lastRejectedCode : event.code,
      };
    case 'request-rejected':
      return { ...current, lastRejectedCode: event.code };
    default:
      return current;
  }
}
